import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { mfaService } from '../../services/mfaService';
import MainNavigation from '../../components/ui/MainNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import EarningsOverview from './components/EarningsOverview';
import ListingCard from './components/ListingCard';
import BookingRequestCard from './components/BookingRequestCard';
import PerformanceMetrics from './components/PerformanceMetrics';
import AddViolationChargeModal from './components/AddViolationChargeModal';
import ViolationChargeCard from './components/ViolationChargeCard';
import MfaSetupModal from './components/MfaSetupModal';
import { violationChargeService } from '../../services/violationChargeService';
import { stripeConnectService } from '../../services/stripeConnectService';
import { bookingService } from '../../services/bookingService';
import { fetchOwnerListings, fetchDeletedListings, reactivateListing } from '../../services/trailerListingService';
import { blockedDatesService } from '../../services/blockedDatesService';
import DashboardSkeleton from '../../components/ui/DashboardSkeleton';
import ErrorFallback from '../../components/ui/ErrorFallback';

const OwnerDashboard = () => {
  const { user, userProfile, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [bookingRequests, setBookingRequests] = useState([]);
  const [earningsData, setEarningsData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [completedBookings, setCompletedBookings] = useState([]);
  const [ratingModalBooking, setRatingModalBooking] = useState(null);
  const [violationCharges, setViolationCharges] = useState([]);
  const [showAddChargeModal, setShowAddChargeModal] = useState(false);
  const [selectedBookingForCharge, setSelectedBookingForCharge] = useState(null);
  const [isLoadingCharges, setIsLoadingCharges] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(true);
  const [w9Status, setW9Status] = useState(null);
  const [isLoadingW9, setIsLoadingW9] = useState(true);
  const [error, setError] = useState(null);
  const [blockedDatesCount, setBlockedDatesCount] = useState(0);
  const [deletedListings, setDeletedListings] = useState([]);
  const [showDeletedListings, setShowDeletedListings] = useState(false);
  const [isLoadingDeleted, setIsLoadingDeleted] = useState(false);
  const [reactivatingId, setReactivatingId] = useState(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (user?.id) {
      checkMfaStatus();
      loadW9Status();
    }
  }, [user]);

  // Real-time subscription for bookings
  useEffect(() => {
    if (!user?.id) return;

    const bookingsChannel = supabase
      ?.channel('owner_bookings_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bookings',
          filter: `owner_id=eq.${user?.id}`
        },
        (payload) => {
          if (payload?.eventType === 'INSERT') {
            // Reload booking data when new booking is created
            bookingService?.fetchOwnerBookings(user?.id)?.then((bookings) => {
              setBookingRequests(bookings?.filter(b => b?.status === 'pending' || b?.status === 'confirmed') || []);
            });
          } else if (payload?.eventType === 'UPDATE') {
            const updatedBooking = payload?.new;
            // Remove from bookingRequests if status changed away from pending/confirmed
            if (updatedBooking?.status !== 'pending' && updatedBooking?.status !== 'confirmed') {
              setBookingRequests((prev) => prev?.filter((b) => b?.id !== updatedBooking?.id));
            } else {
              setBookingRequests((prev) =>
                prev?.map((booking) =>
                  booking?.id === updatedBooking?.id ? { ...booking, ...updatedBooking } : booking
                )
              );
            }
            setCompletedBookings((prev) =>
              prev?.map((booking) =>
                booking?.id === updatedBooking?.id ? { ...booking, ...updatedBooking } : booking
              )
            );
          } else if (payload?.eventType === 'DELETE') {
            // Remove deleted booking
            setBookingRequests((prev) => prev?.filter((b) => b?.id !== payload?.old?.id));
            setCompletedBookings((prev) => prev?.filter((b) => b?.id !== payload?.old?.id));
          }
        }
      )
      ?.subscribe();

    return () => {
      supabase?.removeChannel(bookingsChannel);
    };
  }, [user?.id]);

  // Real-time subscription for violation charges
  useEffect(() => {
    if (!user?.id) return;

    const chargesChannel = supabase
      ?.channel('owner_violation_charges')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'rental_violation_charges',
          filter: `owner_id=eq.${user?.id}`
        },
        (payload) => {
          if (payload?.eventType === 'INSERT') {
            // Add new charge to the list
            setViolationCharges((prev) => [payload?.new, ...prev]);
          } else if (payload?.eventType === 'UPDATE') {
            // Update existing charge
            setViolationCharges((prev) =>
              prev?.map((charge) =>
                charge?.id === payload?.new?.id ? payload?.new : charge
              )
            );
          } else if (payload?.eventType === 'DELETE') {
            // Remove deleted charge
            setViolationCharges((prev) => prev?.filter((c) => c?.id !== payload?.old?.id));
          }
        }
      )
      ?.subscribe();

    return () => {
      supabase?.removeChannel(chargesChannel);
    };
  }, [user?.id]);

  const checkMfaStatus = async () => {
    setMfaLoading(true);
    const { data } = await mfaService?.getMfaStatus(user?.id);
    if (data) {
      setMfaEnabled(data?.mfaEnabled);
    }
    setMfaLoading(false);
  };

  const loadW9Status = async () => {
    setIsLoadingW9(true);
    try {
      const status = await stripeConnectService?.getW9Status();
      setW9Status(status);
    } catch (err) {
      console.error('Failed to load W-9 status:', err);
      setError(err);
    } finally {
      setIsLoadingW9(false);
    }
  };

  const handleMfaSetupSuccess = () => {
    setMfaEnabled(true);
    checkMfaStatus();
  };

  useEffect(() => {
    // Earnings data will be loaded from real-time subscriptions
    setEarningsData({
      monthlyRevenue: 0,
      totalBookings: 0,
      pendingPayouts: 0,
      averageRating: 0,
      monthlyData: []
    });

    // Listings will be loaded from real-time subscriptions
    setListings([]);

    // Booking requests will be loaded from real-time subscriptions
    setBookingRequests([]);

    // Messages will be loaded from real-time subscriptions
    setMessages([]);
  }, []);

  useEffect(() => {
    // Fetch completed bookings that need ratings
    const fetchCompletedBookings = async () => {
      if (!user?.id) return;

      try {
        const { data, error } = await supabase?.from('bookings')?.select(`
            *,
            renter:user_profiles!bookings_renter_id_fkey(id, full_name, email)
          `)?.eq('status', 'completed')?.order('end_date', { ascending: false });

        if (error) throw error;

        // Filter out bookings that already have ratings
        const { data: existingRatings, error: ratingsError } = await supabase?.from('renter_ratings')?.select('booking_id');

        if (ratingsError) throw ratingsError;

        const ratedBookingIds = new Set(existingRatings?.map(r => r?.booking_id));
        const unratedBookings = data?.filter(b => !ratedBookingIds?.has(b?.id));

        setCompletedBookings(unratedBookings || []);
      } catch (error) {
        console.error('Error fetching completed bookings:', error);
      }
    };

    if (isAuthenticated) {
      fetchCompletedBookings();
    }
  }, [user, isAuthenticated]);

  // Fetch violation charges
  useEffect(() => {
    const fetchViolationCharges = async () => {
      if (!user?.id) return;

      try {
        setIsLoadingCharges(true);
        const charges = await violationChargeService?.getOwnerCharges();
        setViolationCharges(charges || []);
      } catch (error) {
        console.error('Error fetching violation charges:', error);
      } finally {
        setIsLoadingCharges(false);
      }
    };

    if (isAuthenticated) {
      fetchViolationCharges();
    }
  }, [user, isAuthenticated]);

  // Fetch owner's trailer listings
  useEffect(() => {
    const fetchListings = async () => {
      if (!user?.id) return;

      try {
        const data = await fetchOwnerListings(user?.id);
        setListings(data || []);
        
        // Fetch blocked dates count
        const blockedDates = await blockedDatesService?.fetchOwnerBlockedDates(user?.id);
        setBlockedDatesCount(blockedDates?.length || 0);
      } catch (error) {
        console.error('Error fetching listings:', error);
      }
    };

    if (isAuthenticated) {
      fetchListings();
    }
  }, [user, isAuthenticated]);

  const loadListings = async () => {
    if (!user?.id) return;
    try {
      const data = await fetchOwnerListings(user?.id);
      setListings(data || []);
    } catch (error) {
      console.error('Error fetching listings:', error);
    }
  };

  const loadDeletedListings = async () => {
    if (!user?.id) return;
    setIsLoadingDeleted(true);
    try {
      const data = await fetchDeletedListings(user?.id);
      setDeletedListings(data || []);
    } catch (error) {
      console.error('Error fetching deleted listings:', error);
    } finally {
      setIsLoadingDeleted(false);
    }
  };

  const handleReactivateListing = async (listingId) => {
    setReactivatingId(listingId);
    try {
      await reactivateListing(listingId);
      setDeletedListings((prev) => prev?.filter((l) => l?.id !== listingId));
      await loadListings();
    } catch (error) {
      console.error('Error reactivating listing:', error);
      alert('Failed to reactivate listing. Please try again.');
    } finally {
      setReactivatingId(null);
    }
  };

  // Real-time subscription for trailer listings
  useEffect(() => {
    if (!user?.id) return;

    const listingsChannel = supabase
      ?.channel('owner_listings_changes')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'trailer_listings',
          filter: `owner_id=eq.${user?.id}`
        },
        (payload) => {
          if (payload?.eventType === 'INSERT') {
            setListings((prev) => [payload?.new, ...prev]);
          } else if (payload?.eventType === 'UPDATE') {
            setListings((prev) =>
              prev?.map((listing) =>
                listing?.id === payload?.new?.id ? payload?.new : listing
              )
            );
          } else if (payload?.eventType === 'DELETE') {
            setListings((prev) => prev?.filter((l) => l?.id !== payload?.old?.id));
          }
        }
      )
      ?.subscribe();

    return () => {
      supabase?.removeChannel(listingsChannel);
    };
  }, [user?.id]);

  // Fetch bookings and calculate earnings
  useEffect(() => {
    const fetchBookingsAndEarnings = async () => {
      if (!user?.id) return;

      try {
        const bookings = await bookingService?.fetchOwnerBookings(user?.id);
        const earnings = bookingService?.calculateEarnings(bookings);
        const payoutData = await bookingService?.fetchPayoutData(user?.id);

        setBookingRequests(bookings?.filter(b => b?.status === 'pending' || b?.status === 'confirmed') || []);
        setEarningsData({
          ...earnings,
          pendingPayouts: payoutData?.pendingPayouts || 0
        });
      } catch (error) {
        console.error('Error fetching bookings and earnings:', error);
      }
    };

    if (isAuthenticated) {
      fetchBookingsAndEarnings();
    }
  }, [user, isAuthenticated]);

  useEffect(() => {
    if (!user?.id) return;

    const loadDashboardData = async () => {
      try {
        setError(null);
        
        // Fetch owner's trailer listings
        const listingsData = await fetchOwnerListings(user?.id);
        setListings(listingsData || []);

        // Fetch bookings
        const bookingsData = await bookingService?.fetchOwnerBookings(user?.id);
        setBookingRequests(bookingsData?.filter(b => b?.status === 'pending' || b?.status === 'confirmed') || []);
        setCompletedBookings(bookingsData?.filter(b => b?.status === 'completed') || []);

        // Calculate earnings
        const earnings = bookingService?.calculateEarnings(bookingsData || []);
        const payoutData = await bookingService?.fetchPayoutData(user?.id);
        
        setEarningsData({
          ...earnings,
          pendingPayouts: payoutData?.pendingPayouts || 0
        });

        // Fetch violation charges
        const chargesData = await violationChargeService?.getOwnerCharges();
        setViolationCharges(chargesData || []);
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError(err);
      }
    };

    loadDashboardData();
  }, [user?.id]);

  const handleRatingSubmit = (ratingData) => {
    // Remove the rated booking from the list
    setCompletedBookings(completedBookings?.filter(b => b?.id !== ratingModalBooking?.id));
    setRatingModalBooking(null);
  };

  const handleAddCharge = (booking) => {
    setSelectedBookingForCharge(booking);
    setShowAddChargeModal(true);
  };

  const handleChargeCreated = async (newCharge) => {
    setViolationCharges(prev => [newCharge, ...prev]);
    setShowAddChargeModal(false);
    setSelectedBookingForCharge(null);
  };

  const handleChargeStatusUpdate = async (chargeId, newStatus) => {
    try {
      const updatedCharge = await violationChargeService?.updateChargeStatus(chargeId, newStatus);
      setViolationCharges(prev => 
        prev?.map(charge => charge?.id === chargeId ? updatedCharge : charge)
      );
    } catch (error) {
      console.error('Error updating charge status:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <MainNavigation isAuthenticated={isAuthenticated} user={user} />
        <div className="pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <DashboardSkeleton />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <MainNavigation isAuthenticated={isAuthenticated} user={user} />
        <div className="pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <ErrorFallback 
              error={error} 
              onRetry={() => window.location?.reload()} 
              title="Failed to load dashboard data"
            />
          </div>
        </div>
      </div>
    );
  }

  const getGreeting = () => {
    const hour = new Date()?.getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const unreadMessages = messages?.filter((m) => m?.unread)?.length || 0;
  const activeListings = listings?.filter((l) => l?.status === 'active')?.length || 0;

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation isAuthenticated={isAuthenticated} user={user} />
      <div className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header Section */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
              {getGreeting()}, {userProfile?.full_name?.split(' ')?.[0] || 'there'}!
            </h1>
            <p className="font-body text-muted-foreground">
              Manage your trailer listings and track your rental business performance.
            </p>
          </div>

          {/* Security Section - MFA */}
          <div className="mb-8">
            <div className="bg-card rounded-lg shadow-elevation-2 p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      mfaEnabled ? 'bg-success/10' : 'bg-warning/10'
                    }`}>
                      <Icon 
                        name="Shield" 
                        size={24} 
                        color={mfaEnabled ? 'var(--color-success)' : 'var(--color-warning)'} 
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-lg font-semibold text-foreground mb-1">
                      Two-Factor Authentication
                    </h3>
                    <p className="font-body text-sm text-muted-foreground mb-3">
                      {mfaEnabled
                        ? 'Your account is protected with two-factor authentication.' :'Add an extra layer of security to your owner account by enabling two-factor authentication.'}
                    </p>
                    {mfaEnabled && (
                      <div className="flex items-center space-x-2 text-success">
                        <Icon name="CheckCircle" size={16} />
                        <span className="font-body text-sm font-medium">Enabled</span>
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  {mfaLoading ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  ) : (
                    <Button
                      variant={mfaEnabled ? 'outline' : 'default'}
                      onClick={() => setShowMfaSetup(true)}
                      iconName={mfaEnabled ? 'Settings' : 'Shield'}
                    >
                      {mfaEnabled ? 'Manage' : 'Enable MFA'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Button
              variant="default"
              size="lg"
              iconName="Plus"
              onClick={() => navigate('/trailer-listing-creation')}
              className="w-full">
              
              Add New Listing
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Calendar"
              onClick={() => document.getElementById('bookings')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full relative">
              
              Booking Requests
              {bookingRequests?.length > 0 &&
              <span className="absolute -top-1 -right-1 bg-error text-error-foreground text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                  {bookingRequests?.length}
                </span>
              }
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="DollarSign"
              onClick={() => navigate('/payout-management')}
              className="w-full">
              
              Payout Management
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="MessageSquare"
              onClick={() => document.getElementById('messages')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-full relative">
              
              Messages
              {unreadMessages > 0 &&
              <span className="absolute -top-1 -right-1 bg-error text-error-foreground text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                  {unreadMessages}
                </span>
              }
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="Settings"
              onClick={() => navigate('/public-user-profile')}
              className="w-full">
              
              Settings
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="FileText"
              onClick={() => navigate('/payout-management')}
              className="w-full relative">
              
              Tax Compliance
              {!isLoadingW9 && w9Status && !w9Status?.w9VerificationCompleted && (
                <span className="absolute -top-1 -right-1 bg-warning text-warning-foreground text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                  !
                </span>
              )}
            </Button>
          </div>

          {/* Earnings Overview */}
          {earningsData && (
            <div className="mb-8">
              <EarningsOverview data={earningsData} />
            </div>
          )}

          {/* Performance Metrics */}
          <div className="mb-8">
            <PerformanceMetrics listings={listings} />
          </div>

          {/* Booking Requests Section */}
          <section id="bookings" className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl font-semibold text-foreground flex items-center space-x-2">
                <Icon name="Calendar" size={24} strokeWidth={2} className="text-primary" />
                <span>Booking Requests</span>
              </h2>
              {bookingRequests?.length > 0 &&
              <span className="px-3 py-1 bg-warning/10 text-warning rounded-full text-sm font-medium">
                  {bookingRequests?.length} pending
                </span>
              }
            </div>
            {bookingRequests?.length > 0 ?
            <div className="space-y-4">
                {bookingRequests?.map((request) =>
              <BookingRequestCard key={request?.id} request={{ ...request, onAddCharge: handleAddCharge }} />
              )}
              </div> :

            <div className="bg-card rounded-lg shadow-elevation-2 p-8 text-center">
                <Icon name="Calendar" size={48} strokeWidth={1.5} className="text-muted-foreground mx-auto mb-4" />
                <p className="font-body text-muted-foreground">No pending booking requests</p>
              </div>
            }
          </section>

          {/* Active Listings Section */}
          <section className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl font-semibold text-foreground flex items-center space-x-2">
                <Icon name="List" size={24} strokeWidth={2} className="text-primary" />
                <span>Your Listings</span>
              </h2>
              <span className="px-3 py-1 bg-success/10 text-success rounded-full text-sm font-medium">
                {activeListings} active
              </span>
            </div>
            {listings?.length > 0 ? (
              <div className="space-y-4">
                {listings?.map((listing) => (
                  <ListingCard key={listing?.id} listing={listing} onUpdate={loadListings} />
                ))}
              </div>
            ) : (
              <div className="bg-card rounded-lg shadow-elevation-2 p-8 text-center">
                <Icon name="Package" size={48} strokeWidth={1.5} className="text-muted-foreground mx-auto mb-4" />
                <p className="font-body text-muted-foreground mb-4">You don't have any listings yet</p>
                <Button variant="default" onClick={() => navigate('/trailer-listing-creation')}>
                  Create Your First Listing
                </Button>
              </div>
            )}

            {/* Deleted Listings Toggle */}
            <div className="mt-4">
              <button
                onClick={() => {
                  const next = !showDeletedListings;
                  setShowDeletedListings(next);
                  if (next && deletedListings?.length === 0) loadDeletedListings();
                }}
                className="flex items-center space-x-2 text-sm text-muted-foreground hover:text-foreground transition-smooth"
              >
                <Icon name={showDeletedListings ? 'ChevronUp' : 'ChevronDown'} size={16} strokeWidth={2} />
                <Icon name="Trash2" size={16} strokeWidth={2} />
                <span>{showDeletedListings ? 'Hide' : 'Show'} Deleted Listings</span>
              </button>

              {showDeletedListings && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading text-lg font-semibold text-foreground flex items-center space-x-2">
                      <Icon name="Archive" size={20} strokeWidth={2} className="text-muted-foreground" />
                      <span>Deleted Listings</span>
                    </h3>
                    <button
                      onClick={loadDeletedListings}
                      className="text-xs text-muted-foreground hover:text-foreground flex items-center space-x-1 transition-smooth"
                    >
                      <Icon name="RefreshCw" size={12} strokeWidth={2} />
                      <span>Refresh</span>
                    </button>
                  </div>

                  {isLoadingDeleted ? (
                    <div className="bg-card rounded-lg shadow-elevation-2 p-6 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    </div>
                  ) : deletedListings?.length > 0 ? (
                    <div className="space-y-3">
                      {deletedListings?.map((listing) => {
                        const primaryPhoto = listing?.photos?.[0] || 'https://img.rocket.new/generatedImages/rocket_gen_img_1e66491e3-1767380697290.png';
                        return (
                          <div
                            key={listing?.id}
                            className="bg-card rounded-lg shadow-elevation-1 overflow-hidden border border-border opacity-75 hover:opacity-100 transition-smooth"
                          >
                            <div className="flex flex-col sm:flex-row">
                              <div className="w-full sm:w-36 h-36 sm:h-auto overflow-hidden flex-shrink-0 relative">
                                <img
                                  src={primaryPhoto}
                                  alt={`${listing?.title} - deleted listing`}
                                  className="w-full h-full object-cover grayscale"
                                />
                                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                                  <span className="px-2 py-1 bg-error/90 text-white text-xs font-medium rounded-full flex items-center space-x-1">
                                    <Icon name="Trash2" size={10} strokeWidth={2} />
                                    <span>Deleted</span>
                                  </span>
                                </div>
                              </div>
                              <div className="flex-1 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div>
                                  <h4 className="font-heading text-base font-semibold text-foreground mb-1">
                                    {listing?.title}
                                  </h4>
                                  <div className="flex items-center space-x-2 mb-1">
                                    <Icon name="MapPin" size={14} strokeWidth={2} className="text-muted-foreground" />
                                    <span className="font-body text-sm text-muted-foreground">
                                      {listing?.locationCity}, {listing?.locationState}
                                    </span>
                                  </div>
                                  <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                                    <span className="font-body font-medium text-foreground">${listing?.dailyRate}/day</span>
                                    <span>·</span>
                                    <span>{listing?.trailerType}</span>
                                  </div>
                                </div>
                                <div className="flex-shrink-0">
                                  <Button
                                    variant="default"
                                    size="sm"
                                    iconName="RotateCcw"
                                    onClick={() => handleReactivateListing(listing?.id)}
                                    loading={reactivatingId === listing?.id}
                                    className="w-full sm:w-auto"
                                  >
                                    Reactivate
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="bg-card rounded-lg shadow-elevation-1 p-6 text-center border border-border">
                      <Icon name="CheckCircle" size={32} strokeWidth={1.5} className="text-muted-foreground mx-auto mb-2" />
                      <p className="font-body text-sm text-muted-foreground">No deleted listings found</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Messages Section */}
          <section id="messages" className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-2xl font-semibold text-foreground flex items-center space-x-2">
                <Icon name="MessageSquare" size={24} strokeWidth={2} className="text-primary" />
                <span>Messages</span>
              </h2>
              <Button
                variant="outline"
                size="sm"
                iconName="ExternalLink"
                onClick={() => navigate('/messaging-center')}
              >
                Open Messaging
              </Button>
            </div>
            <div className="bg-card rounded-lg shadow-elevation-2 divide-y divide-border">
              {messages?.length > 0 ? messages?.map((message) =>
              <div
                key={message?.id}
                onClick={() => navigate('/messaging-center')}
                className={`p-4 flex items-start space-x-3 hover:bg-muted transition-smooth cursor-pointer ${
                message?.unread ? 'bg-primary/5' : ''}`
                }>
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="User" size={20} color="white" strokeWidth={2} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-1">
                      <p className="font-body font-medium text-foreground">{message?.from}</p>
                      <p className="font-caption text-xs text-muted-foreground">{message?.time}</p>
                    </div>
                    <p className="font-body text-sm text-muted-foreground">{message?.message}</p>
                  </div>
                  {message?.unread &&
                <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-2"></div>
                }
                </div>
              ) : (
                <div className="p-8 text-center">
                  <Icon name="MessageSquare" size={40} strokeWidth={1.5} className="text-muted-foreground mx-auto mb-3" />
                  <p className="font-body text-muted-foreground mb-3">No messages yet</p>
                  <Button variant="default" size="sm" onClick={() => navigate('/messaging-center')}>
                    Go to Messaging Center
                  </Button>
                </div>
              )}
            </div>
          </section>

          {/* Payout Information */}
          <section className="mb-8">
            <div className="bg-gradient-to-br from-success to-success/80 rounded-lg shadow-elevation-3 p-6 text-success-foreground">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-heading text-xl font-semibold mb-2">Next Payout</h3>
                  <p className="font-heading text-3xl font-bold mb-1">${earningsData?.pendingPayouts}</p>
                  <p className="font-body text-sm opacity-90">Scheduled for next payout cycle</p>
                </div>
                <div className="w-12 h-12 bg-success-foreground/20 rounded-full flex items-center justify-center">
                  <Icon name="DollarSign" size={24} strokeWidth={2} />
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                iconName="ExternalLink"
                iconPosition="right"
                className="mt-4"
                onClick={() => navigate('/payout-management')}>
                View Payout Details
              </Button>
            </div>
          </section>

          {/* Violation Charges Section */}
          {violationCharges?.length > 0 && (
            <section className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-2xl font-bold text-foreground">Violation Charges</h2>
                <div className="flex items-center space-x-2">
                  <div className="px-3 py-1 bg-primary/10 rounded-full">
                    <span className="font-body text-sm font-medium text-primary">
                      {violationCharges?.filter(c => c?.chargeStatus === 'pending')?.length} Pending
                    </span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4">
                {violationCharges?.slice(0, 5)?.map((charge) => (
                  <ViolationChargeCard
                    key={charge?.id}
                    charge={charge}
                    onStatusUpdate={handleChargeStatusUpdate}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>

      {/* MFA Setup Modal */}
      <MfaSetupModal
        isOpen={showMfaSetup}
        onClose={() => setShowMfaSetup(false)}
        onSuccess={handleMfaSetupSuccess}
      />

      {/* Add Violation Charge Modal */}
      {showAddChargeModal && selectedBookingForCharge && (
        <AddViolationChargeModal
          booking={selectedBookingForCharge}
          onClose={() => {
            setShowAddChargeModal(false);
            setSelectedBookingForCharge(null);
          }}
          onSuccess={handleChargeCreated}
        />
      )}
    </div>
  );

};

export default OwnerDashboard;